import { useEffect, useState } from "react";

/**
 * Nav appearance themes. Each landing-page section declares one via
 * `data-nav-theme`; the navbar adopts the theme of whichever section currently
 * sits under it. `"transparent"` is the over-the-hero default.
 */
export type NavTheme = "transparent" | "white" | "red" | "blue";

const DEFAULT_THEME: NavTheme = "transparent";

/**
 * Tracks which landing-page section is under the navbar and returns that
 * section's declared `data-nav-theme`. A section "owns" the nav once its top
 * edge has scrolled up past the bottom of the nav (so the swap happens exactly
 * as the incoming section's top slides beneath the bar). Picks the lowest such
 * section in document order, which is the one actually covering the nav given
 * the sticky-stacked layout.
 */
export function useSectionNavTheme(navSelector: string): NavTheme {
  const [theme, setTheme] = useState<NavTheme>(DEFAULT_THEME);

  useEffect(() => {
    const sections = Array.from(document.querySelectorAll<HTMLElement>("[data-nav-theme]"));
    if (sections.length === 0) return;
    const nav = document.querySelector<HTMLElement>(navSelector);

    let raf = 0;
    const recompute = () => {
      raf = 0;
      // Bottom edge of the nav in viewport coordinates; sections become active
      // once their top crosses this line.
      const navBottom = nav?.getBoundingClientRect().bottom ?? 0;
      const active = sections.findLast((s) => s.getBoundingClientRect().top <= navBottom);
      const next = (active?.dataset.navTheme as NavTheme | undefined) ?? DEFAULT_THEME;
      setTheme((prev) => (prev === next ? prev : next));
    };
    const schedule = () => {
      if (raf) return;
      raf = requestAnimationFrame(recompute);
    };

    recompute();
    window.addEventListener("scroll", schedule, { passive: true });
    window.addEventListener("resize", schedule);
    return () => {
      window.removeEventListener("scroll", schedule);
      window.removeEventListener("resize", schedule);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [navSelector]);

  return theme;
}
